import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import commonFunctions from '../../utils/commonFunctions';
import './UserModals.css';
import useValidateFields from '../../components/common/useValidateFields';
import API from '../../utils/API';

const UserModal = ({
  setResetUserModalRef,
  user,
  setSuccess,
  closeModal,
  selectedUser, setSelectedUser,
  loadTable, dataMap,
}) => {

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  });

  const [modalError, setModalError] = useState('');

  // User modal field validation states
  const [submitted, setSubmitted] = useState(false);

  const {
    firstNameValid,
    lastNameValid,
    phoneValid,
    emailValid,
  } = useValidateFields(
    {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
    }, selectedUser
  );

  useEffect(() => {
    // console.log('(UserModal.js) selectedUser:', selectedUser)
    if (selectedUser) {
      setFormData({
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        phone: selectedUser.phone,
        email: selectedUser.email,
      });
    }
  }, [selectedUser]);

  useEffect(() => {
    setResetUserModalRef(resetModal);
  }, [setResetUserModalRef]);

  useEffect(() => {
    clearModalError();
  }, [firstNameValid, lastNameValid, phoneValid, emailValid]);

  /**
    * Reset modal
    */
  function resetModal() {
    // console.log('reset modal')
    setSelectedUser(null);
    setSubmitted(false);
    setModalError('');
    // setSuccess('');
    // setError('');
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    });
  }

  /**
   * Handle form input change
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  const handlePhoneChange = useCallback((value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
  }, []);

  /**
   * Add new user to the database
   */
  const handleAddNewUser = async () => {
    const userData = {
      email: formData.email.trim(),
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName.trim(),
      phone: formData.phone,
    };
    if (!userData.email || !userData.lastName) {
      setModalError(<FormattedMessage id="SUBMIT_ERROR" />);
      return;
    }
    try {
      await API.addUser(userData);
      setSuccess(<FormattedMessage id="ADD_USER_SUCCESS" />);
      closeModal("userModal");
      loadTable(dataMap, false);
    } catch (err) {
      setModalError(<FormattedMessage id="ADD_USER_FAILED" values={{ errorMsg: <b>{err.message}</b> }} />);
    }
  };

  /**
   * Edit user in the database
   */
  const handleEditUser = async () => {
    const userData = {
      email: formData.email.trim(),
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName.trim(),
      phone: formData.phone,
      id: selectedUser.id,
    };
    try {
      await API.editUser(userData);
      if (userData.id === user.id && userData.email !== user.email) {
        await API.logout();
        localStorage.removeItem('token');
        window.location = '/login';
        return;
      }
      console.log('edit')
      setSuccess(<FormattedMessage id="EDIT_USER_SUCCESS" />);
      closeModal('userModal');
      loadTable(dataMap, false);
    } catch (err) {
      setModalError(<FormattedMessage id="EDIT_USER_FAILED" values={{ errorMsg: <b>{err.message}</b> }} />);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (
      !firstNameValid &&
      !lastNameValid &&
      !phoneValid &&
      !emailValid
    ) {
      if (selectedUser) {
        handleEditUser();
      } else {
        handleAddNewUser();
      }
    } else {
      setModalError(<FormattedMessage id="SUBMIT_ERROR" />);
    }
  };

  const clearModalError = () => {
    if (
      modalError !== '' &&
      !firstNameValid &&
      !lastNameValid &&
      !phoneValid &&
      !emailValid
    ){
      setModalError('');
    }
  };

  return (
    <div className='modal fade' id='userModal' tabIndex='-1' aria-labelledby='userModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            {selectedUser ? (
              <h5 className='modal-title'><FormattedMessage id='EDIT_USER' /></h5>
            ) : <h5 className='modal-title'><FormattedMessage id='ADD_USER' /></h5>}
            <button id="closeUserModal" type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
          </div>
          <div className='modal-body'>
            <form onSubmit={handleSubmit}>
              <div className='row'>
                <div className='firstNameSection mb-2'>
                  <label htmlFor='firstNameLabel' className='col-form-label'>
                    <FormattedMessage id='FIRST_NAME' />
                  </label>
                  <input
                    id="firstNameInput"
                    onChange={handleChange}
                    value={formData.firstName}
                    name='firstName'
                    className={`form-control ${submitted && (!firstNameValid ? 'is-valid' : 'is-invalid')}`}
                  />
                  {commonFunctions.InvalidFeedbackString(firstNameValid, 'FIRST_NAME')}
                </div>
                <div className='lastNameSection mb-2'>
                  <label htmlFor='lastNameLabel' className='col-form-label'>
                    <FormattedMessage id='LAST_NAME' />
                  </label>
                  <input
                    id="lastNameInput"
                    onChange={handleChange}
                    value={formData.lastName}
                    name='lastName'
                    className={`form-control ${submitted && (!lastNameValid ? 'is-valid' : 'is-invalid')}`}
                  />
                  {commonFunctions.InvalidFeedbackString(lastNameValid, 'LAST_NAME')}
                </div>
                <div className='phoneSection mb-2'>
                  <label htmlFor='phoneLabel' className='col-form-label'>
                    <FormattedMessage id='PHONE' />
                  </label>
                  <PhoneInput
                    id="phoneInput"
                    className={`form-control ${submitted && (!phoneValid ? 'is-valid' : 'is-invalid')}`}
                    placeholder=''
                    value={formData.phone}
                    onChange={handlePhoneChange}
                  />
                  {commonFunctions.InvalidFeedbackString(phoneValid, 'PHONE')}
                </div>
                <div className='emailSection mb-4'>
                  <label htmlFor='emailLabel' className='col-form-label'>
                    <FormattedMessage id='EMAIL' />
                  </label>
                  <input
                    id="emailInput"
                    onChange={handleChange}
                    value={formData.email}
                    name='email'
                    className={`form-control ${submitted && (!emailValid ? 'is-valid' : 'is-invalid')}`}
                  />
                  {commonFunctions.InvalidFeedbackString(emailValid)}
                </div>
                {modalError && (
                  <div className='modal-error invalid-feedback d-block fw-bold text-center'>
                    {modalError}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className='modal-footer justify-content-between'>
            <button id="backUserModalBtn" type='button' className='btn back-btn' data-bs-dismiss='modal'>
              <FormattedMessage id='BACK' />
            </button>
            <button id="saveUserBtn" className='btn save-btn' type='button' onClick={handleSubmit}>
              {selectedUser ? <FormattedMessage id='SAVE' /> : <FormattedMessage id='ADD' />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
